<template>
  <dashboard-page
    title="Configurações"
    subtitle="Dados da unidade, do usuário e das notificações."
  >
    <b-card>
      <b-tabs content-class="pt-4">
        <b-tab title="Unidade" active>
          <div v-if="!editingPlace">
            <div class="d-flex justify-content-end mb-4">
              <button
                type="button"
                class="btn btn-primary"
                @click="startPlaceEdit"
              >
                Editar
              </button>
            </div>
            <div class="configurations-unit">
              <div class="configurations-unit-logo">
                <img
                  v-if="placeLogoUrl"
                  :src="placeLogoUrl"
                  alt="Logotipo"
                />
                <i v-else class="fas fa-image"></i>
              </div>
              <div>
                <h3 class="mb-3">{{ placeName || 'Unidade' }}</h3>
                <p class="mb-2">
                  <span class="text-muted d-block">CNPJ</span>
                  {{ placeCnpj || 'Não informado' }}
                </p>
                <p class="mb-0">
                  <span class="text-muted d-block">Razão Social</span>
                  {{ placeLegalName || 'Não informado' }}
                </p>
              </div>
            </div>
          </div>
          <div v-else>
            <div v-if="!placeFormReady" class="text-muted">
              Nenhuma configuração de unidade disponível.
            </div>
            <EditXFieldModal
              v-else
              layout-mode="page"
              show-cancel
              :object="placeFormObject"
              :x-fields="placeXFields"
              :x-fields-data="placeXFieldsData"
              @cancel="cancelPlaceEdit"
              @emitTmpFiles="emitTmpFiles"
              @saveXFieldsData="savePlaceXFields"
            />
          </div>
        </b-tab>
          <b-tab title="Usuário">
            <p class="text-muted mb-0">
              Nenhuma configuração de usuário disponível.
            </p>
          </b-tab>
          <b-tab title="Notificações" lazy>
            <Mensageria />
          </b-tab>
          <b-tab v-if="hasGetnetModule" title="Pagamentos" lazy>
            <GetnetCredentialsTab :place-uuid="placeUuid" />
          </b-tab>
        </b-tabs>
      </b-card>
  </dashboard-page>
</template>

<script>
import { mapGetters } from 'vuex'
import EditXFieldModal from '@/views/Components/EditXFieldModal.vue'
import GetnetCredentialsTab from '@/views/Configurations/GetnetCredentialsTab.vue'
import Mensageria from '@/views/Mensageria/Mensageria.vue'
import nodeService from '@/services/nodejs'
import {
  publicLogoSource,
  readPublicXField,
} from '@/store/utils/placePublicData'
import { fetchFileObjectUrl } from '@/store/utils/publicLogoFile'
import { hasModule } from '@/store/utils/users'

/**
 * Chaves do xfields da unidade que so as rotas dedicadas podem escrever.
 * `link_getnet_keys` e cifrado pelo backend (ROAD-909); reenviar o valor lido
 * do store pelo PUT generico poderia sobrescrever uma credencial mais nova.
 */
const PROTECTED_PLACE_XFIELDS = ['link_getnet_keys']

export default {
  components: {
    EditXFieldModal,
    GetnetCredentialsTab,
    Mensageria,
  },

  data() {
    return {
      placeXFields: {},
      placeXFieldsData: {},
      placeTmpFiles: {},
      savingPlace: false,
      editingPlace: false,
      placeLogoUrl: null,
      placeLogoObjectUrl: null,
    }
  },

  computed: {
    ...mapGetters('auth', {
      place: 'getPlace',
      user: 'user',
      params: 'params',
    }),

    placeFormReady() {
      return Object.keys(this.placeXFields).length > 0
    },

    placeUuid() {
      return (
        (this.user && this.user.placeUuid) ||
        (this.place && this.place.uuid) ||
        ''
      )
    },

    hasGetnetModule() {
      return hasModule('payment_link_getnet')
    },

    placeFormObject() {
      return {
        type: { value: 'unidade' },
      }
    },

    placeName() {
      return (this.place && this.place.name) || ''
    },

    placeCnpj() {
      return readPublicXField(this.place, 'CNPJ', 'cnpj')
    },

    placeLegalName() {
      return readPublicXField(
        this.place,
        'razao-social',
        'Razão Social',
        'razaosocial'
      )
    },
  },

  watch: {
    place: {
      deep: true,
      handler() {
        this.loadPlaceLogo()
      },
    },
  },

  mounted() {
    this.loadPlaceForm()
    this.loadPlaceLogo()
  },

  beforeDestroy() {
    this.clearPlaceLogo()
  },

  methods: {
    startPlaceEdit() {
      this.loadPlaceForm()
      this.editingPlace = true
    },

    cancelPlaceEdit() {
      this.placeTmpFiles = {}
      this.editingPlace = false
    },

    clearPlaceLogo() {
      if (this.placeLogoObjectUrl) {
        URL.revokeObjectURL(this.placeLogoObjectUrl)
        this.placeLogoObjectUrl = null
      }
    },

    cakeImagePath(src) {
      if (!src || typeof src !== 'string') return ''
      const value = src.trim()
      const display = value.match(/images\/display\/(.+)$/i)
      if (display && display[1]) {
        return decodeURIComponent(display[1]).replace(/[?#].*$/, '').replace(/\/+$/, '')
      }
      const filesGet = value.match(/files\/get\/(.+)$/i)
      if (filesGet && filesGet[1]) {
        return decodeURIComponent(filesGet[1])
          .replace(/[?#].*$/, '')
          .replace(/\/true$/i, '')
          .replace(/\/+$/, '')
          .replace(/ DS /g, '/')
      }
      return ''
    },

    async loadPlaceLogo() {
      this.clearPlaceLogo()
      this.placeLogoUrl = null
      const resolved = publicLogoSource(this.place)
      if (!resolved || !resolved.src) return

      const cakePath = this.cakeImagePath(resolved.src)
      if (resolved.type === 'url' && !cakePath) {
        this.placeLogoUrl = resolved.src
        return
      }

      const objectUrl = await fetchFileObjectUrl(cakePath || resolved.src)
      if (!objectUrl) return
      this.placeLogoObjectUrl = objectUrl
      this.placeLogoUrl = objectUrl
    },

    parseXFields(source) {
      if (!source) return {}
      if (typeof source === 'object') return JSON.parse(JSON.stringify(source))
      try {
        const parsed = JSON.parse(source)
        return parsed && typeof parsed === 'object' ? parsed : {}
      } catch (error) {
        return {}
      }
    },

    loadPlaceForm() {
      const definitions = (this.params && this.params.PlaceXFields) || {}
      const fields = {}

      Object.keys(definitions).forEach((key) => {
        if (!key || !definitions[key]) return
        const field = JSON.parse(JSON.stringify(definitions[key]))
        if (!field.label) field.label = key
        if (
          field.add_display == null &&
          field.type !== 'disabled' &&
          field.type !== 'hidden_config'
        ) {
          field.add_display = '1'
        }
        fields[key] = field
      })

      this.placeXFields = fields
      this.placeXFieldsData = this.formXFieldsData(
        this.place && this.place.xfields
      )
    },

    sameFieldKey(left, right) {
      return String(left || '').toLowerCase() === String(right || '').toLowerCase()
    },

    definitionKeyFor(sourceKey) {
      const keys = Object.keys(this.placeXFields)
      return keys.find((key) => {
        const label = this.placeXFields[key] && this.placeXFields[key].label
        return this.sameFieldKey(key, sourceKey) || this.sameFieldKey(label, sourceKey)
      })
    },

    fieldPayloadKey(sourceKey) {
      const definitionKey = this.definitionKeyFor(sourceKey)
      const definition = definitionKey ? this.placeXFields[definitionKey] : null
      if (definition && typeof definition.label === 'string' && definition.label) {
        return definition.label
      }
      return sourceKey
    },

    dataFieldFor(data, sourceKey) {
      if (!data || !sourceKey) return null
      if (data[sourceKey] && typeof data[sourceKey] === 'object') return data[sourceKey]
      const payloadKey = this.fieldPayloadKey(sourceKey)
      if (data[payloadKey] && typeof data[payloadKey] === 'object') {
        return data[payloadKey]
      }
      const found = Object.keys(data).find((key) => this.sameFieldKey(key, sourceKey))
      return found && typeof data[found] === 'object' ? data[found] : null
    },

    formXFieldsData(source) {
      const data = this.parseXFields(source)
      delete data['']
      const aligned = {}

      Object.keys(this.placeXFields).forEach((key) => {
        const field = this.dataFieldFor(data, key)
        if (field) aligned[key] = field
      })

      return aligned
    },

    emitTmpFiles(tmpFiles) {
      Object.keys(tmpFiles || {}).forEach((slug) => {
        if (tmpFiles[slug] == null) {
          this.$delete(this.placeTmpFiles, slug)
        } else {
          this.$set(this.placeTmpFiles, slug, tmpFiles[slug])
        }
      })
    },

    isUploadableFile(file) {
      if (!(file instanceof Blob) || !file.size) return false
      const name = (file.name || '').toLowerCase()
      const extension = name.includes('.') ? name.split('.').pop() : ''
      const allowed = [
        'pdf',
        'doc',
        'docx',
        'xls',
        'csv',
        'xlsx',
        'jpg',
        'jpeg',
        'png',
        'webp',
        'svg',
        'gif',
      ]
      if (allowed.includes(extension)) return true
      const mime = (file.type || '').toLowerCase()
      return mime === 'image/jpeg' || mime === 'image/png'
    },

    fileFromPickerItem(item, fieldFiles) {
      if (item instanceof Blob) return item
      if (item && item.file instanceof Blob) return item.file
      if (
        item &&
        item.pendingIndex != null &&
        fieldFiles[item.pendingIndex] instanceof Blob
      ) {
        return fieldFiles[item.pendingIndex]
      }
      if (item && item.name && fieldFiles.length) {
        return fieldFiles.find((file) => file && file.name === item.name) || null
      }
      return null
    },

    async uploadPlaceFile(file, name) {
      const formData = new FormData()
      formData.append('file', file)
      const response = await nodeService.postFileV3(
        {
          name: name || file.name,
          refId: (this.place && (this.place.uuid || this.place.id)) || 0,
          ref: 'place',
          status: 1,
        },
        formData
      )
      const data = response && response.data
      const url = data && (data.path || data.url)
      if (!url) {
        throw new Error('Resposta do upload sem url do arquivo')
      }
      return url
    },

    async uploadedFieldValues(payload, pickerFiles) {
      const urlsByKey = {}

      for (const slug of Object.keys(this.placeTmpFiles)) {
        const tmp = this.placeTmpFiles[slug]
        const file = tmp && tmp.file
        if (!this.isUploadableFile(file)) continue
        urlsByKey[slug] = await this.uploadPlaceFile(
          file,
          (tmp && tmp.public_label) || file.name || slug
        )
      }

      for (const key of Object.keys(payload || {})) {
        const value = payload[key] && payload[key].value
        if (!Array.isArray(value)) continue
        const fieldFiles = Array.isArray(pickerFiles && pickerFiles[key])
          ? pickerFiles[key]
          : []
        const urls = []
        let hasUpload = false

        for (const item of value) {
          const file = this.fileFromPickerItem(item, fieldFiles)
          if (this.isUploadableFile(file)) {
            urls.push(await this.uploadPlaceFile(file, file.name || key))
            hasUpload = true
            continue
          }
          if (typeof item === 'string' && item) {
            urls.push(item)
            continue
          }
          if (item && item.kind === 'url' && item.url) urls.push(item.url)
        }

        if (hasUpload) urlsByKey[key] = urls
      }

      return urlsByKey
    },

    fieldStoredValue(field) {
      if (!field || typeof field !== 'object') return field
      if (field.value !== undefined) return field.value
      return field.label
    },

    fieldForSubmit(field) {
      if (!field || typeof field !== 'object' || Array.isArray(field)) return field
      const next = { ...field }
      if (next.value !== undefined) next.label = next.value
      delete next.value
      return next
    },

    storedFieldValue(field, previous) {
      const previousValue = this.fieldStoredValue(previous)
      if (!field || typeof field !== 'object') return previousValue
      const value = field.value !== undefined ? field.value : previousValue
      if (typeof value === 'string' && previousValue && typeof previousValue === 'object') {
        try {
          const parsed = JSON.parse(value)
          if (parsed && typeof parsed === 'object') return parsed
        } catch (error) {
          return value
        }
      }
      return value
    },

    async savePlaceXFields(event) {
      if (this.savingPlace) return
      const placeUuid =
        (this.user && this.user.placeUuid) || (this.place && this.place.uuid)
      if (!placeUuid) {
        this.$notify({
          message: 'Não foi possível identificar a unidade.',
          type: 'warning',
          duration: 5000,
        })
        return
      }

      const current = this.parseXFields(this.place.xfields)
      const payload = (event && event.payload) || {}
      this.savingPlace = true
      let uploaded = {}
      try {
        uploaded = await this.uploadedFieldValues(payload, event && event.files)
      } catch (error) {
        this.savingPlace = false
        this.$notify({
          message: 'Não foi possível enviar o arquivo.',
          type: 'danger',
          duration: 5000,
        })
        return
      }
      const next = {}

      Object.keys(current).forEach((key) => {
        if (!key || PROTECTED_PLACE_XFIELDS.includes(key)) return
        const payloadKey = this.fieldPayloadKey(key)
        if (PROTECTED_PLACE_XFIELDS.includes(payloadKey)) return
        if (next[payloadKey] == null) next[payloadKey] = this.fieldForSubmit(current[key])
      })

      Object.keys(payload).forEach((key) => {
        if (!key || PROTECTED_PLACE_XFIELDS.includes(key)) return
        const payloadKey = this.fieldPayloadKey(key)
        if (PROTECTED_PLACE_XFIELDS.includes(payloadKey)) return
        const previous = this.dataFieldFor(current, key) || {}
        const uploadedKey = Object.keys(uploaded).find(
          (slug) =>
            slug === key ||
            slug === payloadKey ||
            this.sameFieldKey(slug, key) ||
            this.sameFieldKey(slug, payloadKey)
        )
        const uploadedValue = uploadedKey ? uploaded[uploadedKey] : undefined
        next[payloadKey] = {
          type: previous.type || (payload[key] && payload[key].type) || '',
          label:
            uploadedValue !== undefined
              ? uploadedValue
              : this.storedFieldValue(payload[key], previous),
        }
      })

      this.savingPlace = true
      try {
        const response = await nodeService.updatePlaceXFields(placeUuid, next)
        const body = (response && response.data) || {}
        const samePlace =
          body.status === 'ok' &&
          body.place_uuid &&
          String(body.place_uuid).toUpperCase() ===
            String(placeUuid).toUpperCase()

        if (samePlace) {
          this.placeTmpFiles = {}
          this.editingPlace = false
          this.$store.commit('auth/SET_PLACE_XFIELDS', body)
          this.placeXFieldsData = this.formXFieldsData(body.xfields)
          this.$notify({
            message: 'Configurações da unidade salvas.',
            type: 'success',
            duration: 4000,
          })
        } else {
          this.$notify({
            message: 'A unidade salva não é a do usuário logado.',
            type: 'warning',
            duration: 5000,
          })
        }
      } catch (error) {
        this.$notify({
          message: 'Não foi possível salvar as configurações da unidade.',
          type: 'danger',
          duration: 5000,
        })
      } finally {
        this.savingPlace = false
      }
    },
  },
}
</script>

<style>
.configurations-unit {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.configurations-unit-logo {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  flex: 0 0 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f6f9fc;
  color: #adb5bd;
  font-size: 2rem;
}

.configurations-unit-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media (max-width: 575.98px) {
  .configurations-unit {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
